<script lang="ts">
	import type { EligibleProductsResponse } from '$lib/types/special-sale';
	import type { ProductCardMissingInfo$result } from '$houdini';
	import SaleChip from '$lib/components/Product/SaleChip.svelte';
	import { getTranslate } from '@tolgee/svelte';
	import { page } from '$app/stores';
	import ProductPriceRange from '$lib/components/Product/ProductPriceRange.svelte';
	import ProductPrice from '$lib/components/Product/ProductPrice.svelte';
	import ButtonSecondary from '$lib/components/Button/ButtonSecondary.svelte';
	import { cart } from '$lib/store/cart.store';
	import ProductAddedToCartDrawer from '$lib/components/Drawer/ProductAddedToCartDrawer.svelte';
	import ProductBundleRecommendationCard from '$lib/components/Product/SpecialSale/ProductBundleRecommendationCard.svelte';
	import { recordAddToCart } from '$lib/posthog';
	import { mapToNode, getPriceAfterDiscount } from '$lib/utils/product';
	import { sumMoney } from '$lib/utils';

	export let sale: EligibleProductsResponse;
	export let productData: ProductCardMissingInfo$result;

	let addedLines: string[] = [];
	let addedDrawerExpanded = false;
	let hasCompletedSelection = false;

	const { t } = getTranslate();

	let sku: string | undefined;
	$: selectedVariant = mapToNode(productData.product?.variants).find((s) => s.sku?.value === sku);
	$: sdsVariant = mapToNode(productData.sdsProduct?.variants).find((s) => s.sku?.value === sku);

	$: priceData = (() => {
		if (selectedVariant) {
			return {
				price: sumMoney(
					getPriceAfterDiscount(selectedVariant.price, sale.discountPercentage),
					$page.data.product.variant.price
				),
				compareAtPrice: sumMoney(
					selectedVariant.compareAtPrice,
					$page.data.product.variant.compareAtPrice
				)
			};
		} else {
			return {
				priceRange: {
					minVariantPrice: sumMoney(
						$page.data.product.variant.price,
						getPriceAfterDiscount(
							productData.product?.priceRange.minVariantPrice,
							sale.discountPercentage
						)
					),
					maxVariantPrice: sumMoney(
						$page.data.product.variant.price,
						getPriceAfterDiscount(
							productData.product?.priceRange.maxVariantPrice,
							sale.discountPercentage
						)
					)
				},
				compareAtPriceRange: {
					minVariantPrice: sumMoney(
						$page.data.product.variant.compareAtPrice,
						productData.product?.compareAtPriceRange.minVariantPrice
					),
					maxVariantPrice: sumMoney(
						$page.data.product.variant.compareAtPrice,
						productData.product?.compareAtPriceRange.maxVariantPrice
					)
				}
			};
		}
	})();

	$: if (productData.product?.is_single?.value === 'true') {
		sku = productData.product?.variants?.edges?.[0].node.sku?.value;
		hasCompletedSelection = true;
	}

	$: sdsPageVariant = mapToNode($page.data.sdsProduct?.variants).find(
		(variant) => variant.sku?.value === $page.url.searchParams.get('variantSKU')
	);

	$: parentHasStock =
		$cart.getRealQuantity(
			$page.data.variant?.sku?.value || '',
			Math.max(sdsPageVariant?.quantityAvailable ?? 0, $page.data.variant?.quantityAvailable ?? 0)
		) > 0;
</script>

{#if productData?.product}
	<div class="flex justify-between">
		<h3>{$t('bundles.save-money')}</h3>
		<SaleChip class="!font-bold" sale={sale.discountPercentage * 100} />
	</div>
	<h6 class="my-4">
		{$page.data.product.title} + {productData?.product?.title}
	</h6>
	<ProductBundleRecommendationCard
		data={productData}
		discountedPercentage={sale.discountPercentage}
		bind:sku
		on:complete={() => (hasCompletedSelection = true)}
		on:incomplete={() => (hasCompletedSelection = false)}
	/>
	<h5>{$t('bundles.total-price')}</h5>
	{#if priceData.priceRange}
		<ProductPriceRange
			priceRange={priceData.priceRange}
			compareAtPriceRange={priceData.compareAtPriceRange}
		/>
	{:else}
		<ProductPrice price={priceData.price} compareAtPrice={priceData.compareAtPrice} />
	{/if}
{/if}
<div class="flex justify-center my-2">
	<ButtonSecondary
		on:click={async () => {
			if (!selectedVariant?.id || !selectedVariant?.sku?.value || !sdsVariant) {
				return;
			}

			recordAddToCart({
				sku: selectedVariant?.sku?.value || '',
				source: 'Product Detail Page Bundle Y',
				quantity: 1,
				handle: productData?.product?.handle || ''
			});
			const linesIds = await cart
				.addSDSItem({
					sku: selectedVariant.sku?.value || '',
					variantId: selectedVariant.id,
					sdsId: sdsVariant.id,
					sdsThreshold: Number(selectedVariant.sds_qty?.value || '10') || 10,
					quantity: 1,
					handle: productData?.product?.handle || '',
					sdsOnHand: sdsVariant.quantityAvailable || 0,
					avantradoOnHand: selectedVariant.quantityAvailable || 0
				})
				.then((first) => {
					if (!sdsPageVariant) {
						return [first];
					}
					recordAddToCart({
						sku: $page.data.product.variant.sku?.value || '',
						source: 'Product Detail Page Bundle X',
						quantity: 1,
						handle: $page.data.product.handle
					});

					return cart
						.addSDSItem({
							sku: $page.data.product.variant.sku?.value || '',
							variantId: $page.data.product.variant.id,
							sdsId: sdsPageVariant.id,
							sdsThreshold: Number($page.data.product.variant.sds_qty?.value || '10') || 10,
							quantity: 1,
							handle: $page.data.product.handle,
							sdsOnHand: sdsPageVariant.quantityAvailable || 0,
							avantradoOnHand: sdsPageVariant.quantityAvailable || 0
						})
						.then((second) => [first, second]);
				});

			addedLines = linesIds.filter(Boolean);
			addedDrawerExpanded = true;
		}}
		disabled={!sku || !hasCompletedSelection || !parentHasStock}
		text={$t('bundles.action.add-to-cart')}
	/>
</div>
<ProductAddedToCartDrawer lineIds={addedLines} bind:expanded={addedDrawerExpanded} />
